import { UseFormReturn } from 'react-hook-form';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { HighlightEditor } from '@/components/ui/highlight-editor';
import { HighlightableSelect } from '@/components/ui/highlightable-select';
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ResponsiveImageUpload } from '../shared/responsive-image-upload';
import { useState, useRef } from 'react';
import { useToast } from '@/hooks/use-toast';
import { uploadSiteSurveyPhoto } from '../shared/site-survey-photo-upload';

interface EquipmentLocationProps {
  form: UseFormReturn<any>;
  installationId?: number;
  surveyId?: number;
  onPhotoUploaded?: () => void;
}

export function EquipmentLocation({
  form,
  installationId,
  surveyId,
  onPhotoUploaded,
}: EquipmentLocationProps) {
  const [uploadingImages, setUploadingImages] = useState<Record<string, boolean>>({});
  const { toast } = useToast();
  const fileInputRefs = {
    proposedAreaPhoto: useRef<HTMLInputElement>(null),
  };

  const isMobileDevice = () => {
    return (
      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
      (navigator.maxTouchPoints && navigator.maxTouchPoints > 2)
    );
  };

  const handleImageUpload = async (field: string, file: File) => {
    setUploadingImages((prev) => ({ ...prev, [field]: true }));

    try {
      await uploadSiteSurveyPhoto({
        form,
        field,
        file,
        surveyId,
        installationId,
        customerId: form.getValues('customerId'),
        description: `Equipment Location ${field} photo`,
        toast,
        onPhotoUploaded,
      });
    } catch (error) {
      console.error('Upload error:', error);
      toast({
        title: 'Upload Failed',
        description:
          error instanceof Error ? error.message : 'Failed to upload image. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setUploadingImages((prev) => ({ ...prev, [field]: false }));
    }
  };

  const handleFileSelect = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      handleImageUpload(field, file);
    }
    event.target.value = '';
  };

  const handleCameraCapture = (field: string) => {
    const input = fileInputRefs[field as keyof typeof fileInputRefs];
    if (input.current) {
      if (isMobileDevice()) {
        input.current.setAttribute('capture', 'environment');
      } else {
        input.current.removeAttribute('capture');
      }
      input.current.click();
    }
  };

  const triggerFileUpload = (field: string) => {
    const input = fileInputRefs[field as keyof typeof fileInputRefs];
    if (input.current) {
      input.current.removeAttribute('capture');
      input.current.click();
    }
  };

  const renderImageUpload = (fieldName: string, label: string) => {
    return (
      <ResponsiveImageUpload
        form={form}
        fieldName={fieldName}
        label={label}
        isUploading={uploadingImages[fieldName]}
        onCameraCapture={() => handleCameraCapture(fieldName)}
        onFileUpload={() => triggerFileUpload(fieldName)}
        onPhotoDeleted={onPhotoUploaded}
      />
    );
  };

  return (
    <div className="space-y-6">
      {Object.entries(fileInputRefs).map(([key, ref]) => (
        <input
          key={key}
          ref={ref}
          type="file"
          accept="image/*,.heic,.heif"
          style={{ display: 'none' }}
          onChange={handleFileSelect(key)}
        />
      ))}

      <Card>
        <CardHeader>
          <CardTitle>Equipment Location</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="proposedArea"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Proposed Area</FormLabel>
                  <FormControl>
                    <HighlightableSelect
                      value={field.value || ''}
                      onValueChange={field.onChange}
                      placeholder="Select proposed area"
                      options={[
                        { value: 'OUTDOOR_SIDE', label: 'Outdoor Side' },
                        { value: 'OUTDOOR_REAR', label: 'Outdoor Rear' },
                        { value: 'GARAGE', label: 'Garage' },
                        { value: 'UTILITY', label: 'Utility' },
                        { value: 'PLANT_ROOM', label: 'Plant Room' },
                        { value: 'KITCHEN', label: 'Kitchen' },
                        { value: 'OFFICE', label: 'Office' },
                        { value: 'OTHER', label: 'Other' },
                      ]}
                      fieldName="proposedArea"
                      form={form}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="proposedAreaNotes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Proposed Area Notes</FormLabel>
                  <FormControl>
                    <HighlightEditor
                      value={field.value || ''}
                      onChange={field.onChange}
                      placeholder="Additional notes about the proposed area..."
                      savedStatus={!!surveyId}
                      minHeight="100px"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          {renderImageUpload('proposedAreaPhoto', 'Proposed Area Photo')}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="overboardWall"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Overboard Wall</FormLabel>
                  <FormControl>
                    <HighlightableSelect
                      value={field.value || ''}
                      onValueChange={field.onChange}
                      placeholder="Select option"
                      options={[
                        { value: 'NO', label: 'No' },
                        { value: 'REQUIRED', label: 'Required' },
                      ]}
                      fieldName="overboardWall"
                      form={form}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="canopy"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Canopy</FormLabel>
                  <FormControl>
                    <HighlightableSelect
                      value={field.value || ''}
                      onValueChange={field.onChange}
                      placeholder="Select option"
                      options={[
                        { value: 'NO', label: 'No' },
                        { value: 'REQUIRED', label: 'Required' },
                      ]}
                      fieldName="canopy"
                      form={form}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="equipmentHeight"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Height</FormLabel>
                  <FormControl>
                    <Input
                      type="text"
                      {...field}
                      value={field.value || ''}
                      placeholder="Enter height..."
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="equipmentWidth"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Width</FormLabel>
                  <FormControl>
                    <Input
                      type="text"
                      {...field}
                      value={field.value || ''}
                      placeholder="Enter width..."
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <FormField
            control={form.control}
            name="equipmentNotes"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Notes</FormLabel>
                <FormControl>
                  <HighlightEditor
                    value={field.value || ''}
                    onChange={field.onChange}
                    placeholder="Additional notes about equipment location..."
                    savedStatus={!!surveyId}
                    minHeight="150px"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="scopeOfWorksInverterAC"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Scope of Works – Inverter and AC</FormLabel>
                <FormControl>
                  <HighlightEditor
                    value={field.value || ''}
                    onChange={field.onChange}
                    placeholder="Describe the scope of works for inverter and AC installation..."
                    savedStatus={!!surveyId}
                    minHeight="200px"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </CardContent>
      </Card>
    </div>
  );
}
